import {
  FilterInterfaceFilter,
  FilterTypes,
  FormattingFilter,
  ReferenceLabelFormattingAttributes,
} from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';

/**
 * Checks if a given filter is a formatting filter, meaning it has the color
 * attribute set, in order to distinguish them from label filters which are
 * technically handled as formatting filters.
 */
export const isStrictlyFormattingFilter = (
  conditionalFormatting: FormattingFilter | FilterInterfaceFilter
): conditionalFormatting is FormattingFilter => {
  return (conditionalFormatting as FormattingFilter).color !== undefined;
};

export const isComponentFilter = (
  filter: FormattingFilter | FilterInterfaceFilter
): boolean => {
  return filter.affectComponent;
};

export const isReferenceFilter = (
  filter: FormattingFilter | FilterInterfaceFilter
): boolean => {
  return filter.affectReference;
};

export const isComponentFormatting = (
  conditionalFormatting: FormattingFilter | FilterInterfaceFilter
): boolean => {
  return (
    isStrictlyFormattingFilter(conditionalFormatting) &&
    isComponentFilter(conditionalFormatting)
  );
};

export const isReferenceFormatting = (
  conditionalFormatting: FormattingFilter | FilterInterfaceFilter
): boolean => {
  return (
    isStrictlyFormattingFilter(conditionalFormatting) &&
    isReferenceFilter(conditionalFormatting)
  );
};

export const isReferenceLabelFormattingFilter = (
  filter: FormattingFilter | FilterInterfaceFilter
): filter is FormattingFilter & ReferenceLabelFormattingAttributes =>
  filter.type === FilterTypes.REFERENCE_LABEL;

export const getColor = (filter: FormattingFilter) => filter.color;

const hasColorProperty = (value: unknown): value is { color: string } =>
  typeof value === 'object' && value !== null && 'color' in value;
/** because list field filters can have multiple colors. */
export const getColors = (filter: FormattingFilter): string | string[] => {
  const { value, color } = filter;
  if (value && typeof value === 'object') {
    return Object.values(value)
      .map(value => (hasColorProperty(value) ? value.color : undefined))
      .filter(isTruthy);
  }
  return color;
};
